import { Avatar } from '@zappyrent/hundop';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import shallow from 'zustand/shallow';

import { convertFileToBase64 } from '../../../shared/helpers';
import { IUser } from '../../../shared/ts/user';
import useStore from '../../../store';
import DefaultAvatar from '../../DefaultAvatar';
import {
  StyledAvatar,
  StyledButton,
  StyledInput,
  StyledNameContainer,
  StyledTitle,
  StyledWrapper,
} from './UserAvatar.styles';

const UserAvatar = (): JSX.Element => {
  const { t } = useTranslation(['a11y', 'translation']);
  const { updateUserById, user } = useStore(
    state => ({
      updateUserById: state.updateUserById,
      user: state.user,
    }),
    shallow,
  );
  const { id, first_name, img, last_name, type } = user as IUser;
  const fileInput = useRef<HTMLInputElement | null>(null);
  const handleOnButtonClick = () => {
    fileInput?.current?.click();
  };
  const handleOnInputClick = (
    event: React.MouseEvent<HTMLInputElement, MouseEvent>,
  ) => {
    const element = event.target as HTMLInputElement;
    element.value = '';
  };
  const handleOnInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file: File = (e.target.files as FileList)[0];
    const { size: fileSize, type: fileType } = file;

    if (fileType !== 'image/jpeg' && fileType !== 'image/png') {
      alert(t('translation:uploadedImageFormatIsNotValid'));
      return;
    }

    if (fileSize >= 1000000) {
      alert(t('translation:uploadedImageIsTooBig'));
      return;
    }

    try {
      const base64Image = await convertFileToBase64(file);
      const payload = { img: base64Image } as IUser;

      updateUserById(id, payload);

      e.target.value = '';
    } catch (err) {
      console.error(err);
    }

    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <StyledWrapper>
      <StyledNameContainer>
        <StyledTitle sm="24px" md="24px" lg="24px" tag="h3">
          {first_name} {last_name}
        </StyledTitle>
        <StyledButton onClick={handleOnButtonClick} user={type}>
          {t('a11y:uploadANewPicture')}
        </StyledButton>
        <StyledInput
          accept="image/jpeg, image/png"
          id="user_avatar"
          ref={fileInput}
          type="file"
          onClick={handleOnInputClick}
          onChange={handleOnInputChange}
        />
      </StyledNameContainer>
      <StyledAvatar user={type}>
        {img ? (
          <Avatar alt={t('a11y:userProfileImage')} src={img} />
        ) : (
          <DefaultAvatar title={t('a11y:noUserProfileImage')} />
        )}
      </StyledAvatar>
    </StyledWrapper>
  );
};

export default UserAvatar;
